import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ActiveBadge } from '@/modules/inventory/components';
import { useClients } from '@/modules/commercial/hooks';
import { saveClient } from '@/modules/commercial/services';
import {
  CLIENT_TYPE_LABELS,
  type Client,
  type ClientInput,
  type ClientType,
} from '@/modules/commercial/types';
import { filterClients } from '@/modules/commercial/utils/operationFilters';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const empty: ClientInput = {
  type: 'individual',
  name: '',
  businessName: '',
  taxId: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  active: true,
};

export function ClientsPage() {
  const { clients, loading, error, refresh } = useClients();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('commercial.write');

  const [form, setForm] = useState<ClientInput>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>(
    'all',
  );
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => filterClients(clients, { active: activeFilter, search }),
    [clients, activeFilter, search],
  );

  function startEdit(client: Client) {
    setEditingId(client.id);
    setForm({
      type: client.type,
      name: client.name,
      businessName: client.businessName,
      taxId: client.taxId,
      email: client.email,
      phone: client.phone,
      address: client.address,
      notes: client.notes,
      active: client.active,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(empty);
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !form.name.trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveClient(form, editingId ?? undefined);
      resetForm();
      await refresh();
    } catch {
      setFormError('No se pudo guardar el cliente.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <p>
        <Link to={modulePaths.commercial}>← Comercial</Link>
      </p>
      <h1>Clientes</h1>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Estado
          <select
            value={activeFilter}
            onChange={(e) =>
              setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')
            }
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </label>
        <label className="auth-label">
          Buscar
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nombre, email, CUIT"
          />
        </label>
      </div>

      {canWrite ? (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Editar cliente' : 'Nuevo cliente'}</h2>
          <div className="users-form-grid">
            <label className="auth-label">
              Tipo
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({ ...form, type: e.target.value as ClientType })
                }
              >
                {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((t) => (
                  <option key={t} value={t}>
                    {CLIENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-label">
              Nombre *
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Razón social
              <input
                value={form.businessName}
                onChange={(e) =>
                  setForm({ ...form, businessName: e.target.value })
                }
              />
            </label>
            <label className="auth-label">
              CUIT / DNI
              <input
                value={form.taxId}
                onChange={(e) => setForm({ ...form, taxId: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Email
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Teléfono
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
          </div>
          <label className="auth-label">
            Dirección
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Notas
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Activo
          </label>
          {formError ? <p className="auth-error">{formError}</p> : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            {editingId ? (
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={resetForm}
              >
                Cancelar edición
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      <table className="users-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>Contacto</th>
            <th>Estado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>{CLIENT_TYPE_LABELS[c.type]}</td>
              <td>
                {c.email || c.phone || '—'}
              </td>
              <td>
                <ActiveBadge active={c.active} />
              </td>
              <td>
                {canWrite ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => startEdit(c)}
                  >
                    Editar
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">No hay clientes con los filtros aplicados.</p>
      ) : null}
    </section>
  );
}
